import React from 'react';
import { Card } from '@/components/ui/card';
import ContactDisplay from '@/components/common/ContactDisplay';
import { Booking } from '@/types/Booking';
import { Service } from '@/types/Service';
import { Contact } from '@/types/Contact';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useDateFormat } from '@/hooks/useDateFormat';

interface AppointmentCardProps {
  booking: Booking;
  service?: Service | null;
  contact?: Contact | null;
  contacts?: Contact[];
  onClick?: (e?: React.MouseEvent) => void;
  className?: string;
  variant?: 'default' | 'slot';
  showDate?: boolean;
  isSelected?: boolean;
}

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
  booking,
  service,
  contact,
  contacts = [],
  onClick,
  className,
  variant = 'default',
  showDate = false,
  isSelected = false,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();

  const startTime = parseISO(booking.start_time);
  const endTime = parseISO(booking.end_time);
  const dateLabel = df.dateWithWeekday(startTime);
  const timeRange = `${df.time(startTime)} - ${df.time(endTime)}`;
  const serviceName =
    service?.name ||
    t('agent.calendar.appointment.no_service.translation', 'No Service');
  const isCancelled = booking.status.toLowerCase() === 'cancelled';

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
    }
  };

  const statusBorderColor = getStatusColor(booking.status).includes('green')
    ? '#10b981'
    : getStatusColor(booking.status).includes('yellow')
      ? '#eab308'
      : getStatusColor(booking.status).includes('red')
        ? '#ef4444'
        : undefined;

  const statusSelectionRingClass = (() => {
    switch (booking.status.toLowerCase()) {
      case 'confirmed':
        return 'ring-2 ring-emerald-400/70 dark:ring-emerald-500/60';
      case 'pending':
        return 'ring-2 ring-amber-400/70 dark:ring-amber-500/60';
      case 'cancelled':
        return 'ring-2 ring-rose-400/70 dark:ring-rose-500/60';
      default:
        return 'ring-2 ring-blue-400/70 dark:ring-blue-500/60';
    }
  })();

  const statusSurfaceClass = (() => {
    switch (booking.status.toLowerCase()) {
      case 'confirmed':
        return 'bg-emerald-50/80 hover:bg-emerald-100/80 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30';
      case 'pending':
        return 'bg-amber-50/85 hover:bg-amber-100/80 dark:bg-amber-900/20 dark:hover:bg-amber-900/30';
      case 'cancelled':
        return 'bg-rose-50/80 hover:bg-rose-100/80 dark:bg-rose-900/20 dark:hover:bg-rose-900/30';
      default:
        return 'bg-card hover:bg-[hsl(var(--calendar-appointment-hover))]';
    }
  })();

  if (variant === 'slot') {
    return (
      <Card
        className={cn(
          'relative flex h-full min-h-[22px] cursor-pointer items-center overflow-hidden rounded-md py-0 pl-2 pr-1 transition-all',
          statusSurfaceClass,
          isSelected ? statusSelectionRingClass : '',
          className
        )}
        onClick={handleClick}
        title={`${serviceName} · ${timeRange}`}
      >
        {statusBorderColor && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-[3px] rounded-l-md"
            style={{ backgroundColor: statusBorderColor }}
          />
        )}
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[10px] font-semibold leading-tight sm:text-[11px]',
            isCancelled
              ? 'text-muted-foreground line-through'
              : 'text-foreground'
          )}
        >
          {serviceName}
        </span>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'relative cursor-pointer transition-all h-full overflow-hidden flex flex-col rounded-md py-2 pl-3 pr-2',
        statusSurfaceClass,
        isSelected ? statusSelectionRingClass : '',
        className
      )}
      onClick={handleClick}
    >
      {statusBorderColor && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 bottom-0 w-[3px] rounded-l-md"
          style={{ backgroundColor: statusBorderColor }}
        />
      )}
      <div className="flex-1 min-h-0 flex flex-col">
        <div
          className={cn(
            'font-semibold text-xs truncate mb-0.5',
            isCancelled
              ? 'text-muted-foreground line-through'
              : 'text-foreground'
          )}
        >
          {serviceName}
        </div>
        {contact && (
          <div
            className={cn(
              'mb-1 min-w-0 truncate text-xs',
              isCancelled ? 'text-muted-foreground/80' : 'text-muted-foreground'
            )}
          >
            <ContactDisplay
              callerId={contact.caller_id}
              agentId={booking.agent_id}
              contacts={contacts.length > 0 ? contacts : [contact]}
              variant="compact"
              className="text-xs font-normal"
            />
          </div>
        )}
        {showDate && (
          <div className="text-xs text-muted-foreground font-medium mb-0.5">
            {dateLabel}
          </div>
        )}
        <div
          className={cn(
            'text-xs font-medium mb-1',
            isCancelled
              ? 'text-muted-foreground line-through'
              : 'text-muted-foreground'
          )}
        >
          {timeRange}
        </div>
        {booking.customer_notes && (
          <div className="text-xs text-muted-foreground line-clamp-2 flex-1 overflow-hidden">
            {booking.customer_notes}
          </div>
        )}
      </div>
    </Card>
  );
};
